import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import sizes from 'react-sizes';
import { Markup } from 'interweave';
import { remark } from 'remark';
import remarkHTML from 'remark-html';
import {
  ActivityInfo,
  ActivityFeatures,
  ClientTestimonialsMultiCarousel,
  ContactUs,
  GetQuoteModal,
  ProgramaticallyCreatedPageOverview,
  Hero,
  HeroImg,
  SEO,
  SimilarCards,
  FeaturedVideo,
  KeyBenefits,
  HowItWorks,
  WhyChooseThisActivity,
  LargeContent,
  NotPaddedMultiCarousel,
  FAQ,
  HighlightBanner
} from 'components';
import CallToActions from 'components/Activity/CallToActions';
import { OtherLocations } from 'components/Location';
import { OtherEventTypes } from 'components/EventType';
import camelCase from 'just-camel-case';
import { useScrollLock } from 'hooks';
import BlobYellowSVG from 'images/event_management/blob_yellow.svg';

export default sizes(({ width }) => ({ viewportWidth: width && width }))(
  ({ data, location, handleHeader, handleEventManagementTrendsBannerLoading, viewportWidth }) => {
    const { pathname } = location;
    const {
      activity,
      similarActivities,
      newCaseStudies,
      otherLocations,
      relatedActivities,
      locationsGridOrder,
      teamBuildingEventTypes
    } = data;
    const { html, frontmatter } = activity.edges[0].node.childMarkdownRemark;
    const { lockScroll, unlockScroll } = useScrollLock();
    const {
      activityLocation,
      approach,
      pardotRedirectLinkForBrochure,
      carouselImgs,
      secondaryCarouselImgs,
      duration,
      featuredImg,
      fees,
      keyBusinessBenefits,
      numbers,
      summary,
      title,
      specificTestimonials,
      productId,
      introduction,
      featureVideo,
      keyBenefits,
      howItWorks,
      whyChooseThisActivity,
      largeContent,
      faqs,
      relatedActivitiesIntroduction,
      highlightedBanner,
      callToActionBanners
    } = frontmatter;

    let { formatOfDeliveryAndEventSupport } = frontmatter;

    useEffect(() => {
      const eventManagementBannerEC = document.querySelector('.event-management-banner-ec');
      if (eventManagementBannerEC) {
        handleEventManagementTrendsBannerLoading();
      }
    }, []);

    /* Removes any formats who's corresponding fees = 0. This can happen if a format once had fees
      but then those fees were later removed. */
    if (fees) {
      formatOfDeliveryAndEventSupport = formatOfDeliveryAndEventSupport.filter((format) => {
        const formatTypeInCamelCase = camelCase(format);
        return fees[formatTypeInCamelCase]?.setupFee && fees[formatTypeInCamelCase]?.perPlayerFee;
      });
    }

    const brochureUrl = pardotRedirectLinkForBrochure && pardotRedirectLinkForBrochure;

    const getQuoteModalRef = useRef(null);

    const handleGetQuoteButtonClick = () => {
      if (Array.from(getQuoteModalRef.current.classList).includes('open')) {
        unlockScroll();
        getQuoteModalRef.current.close();
      } else {
        lockScroll();
        getQuoteModalRef.current.showModal();
      }
    };

    const toHTML = (value) => remark().use(remarkHTML).processSync(value).toString();

    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
      target: containerRef,
      offset: ['start end', 'end start']
    });
    const y = useTransform(scrollYProgress, [0, 0.05], [150, 0]);

    return (
      title && (
        <>
          <Hero
            title={title}
            subText={summary}
            handleClick={handleGetQuoteButtonClick}
            fees={fees}
            buttonText="Get Quote"
            buttonWidth="150px"
            buttonHeight="44px"
            handleHeader={handleHeader}>
            <HeroImg heroImg={featuredImg} />
          </Hero>
          <ActivityFeatures
            features={{
              formatOfDeliveryAndEventSupport: frontmatter.formatOfDeliveryAndEventSupport,
              approach,
              duration,
              numbers,
              activityLocation
            }}
          />
          <motion.div ref={containerRef} style={{ y }}>
            {introduction && introduction.title && (
              <Introduction>
                <h2>{introduction.title}</h2>
                <Markup content={toHTML(introduction.paragraph)} />
              </Introduction>
            )}
            {featureVideo && featureVideo.embedLink && (
              <FeaturedVideo video={featureVideo} viewportWidth={viewportWidth} />
            )}
            {keyBenefits && <KeyBenefits keyBenefits={keyBenefits} />}
            <ProgramaticallyCreatedPageOverview
              pathname={pathname}
              carouselImgs={carouselImgs}
              lessPadding
              reverse={viewportWidth < 1024}>
              {howItWorks ? (
                <HowItWorks howItWorks={howItWorks} />
              ) : (
                <ActivityInfo
                  handleClick={handleGetQuoteButtonClick}
                  buttonLinkPath={brochureUrl}
                  info={{
                    fees,
                    formatOfDeliveryAndEventSupport,
                    approach,
                    duration,
                    numbers,
                    activityLocation,
                    html,
                    keyBusinessBenefits
                  }}
                />
              )}
            </ProgramaticallyCreatedPageOverview>
            {whyChooseThisActivity && (
              <ProgramaticallyCreatedPageOverview
                lessPadding
                pathname={pathname}
                carouselImgs={secondaryCarouselImgs}
                reverse>
                <WhyChooseThisActivity whyChooseThisActivity={whyChooseThisActivity} />
              </ProgramaticallyCreatedPageOverview>
            )}
            {viewportWidth < 1024 && (
              <>
                <br />
                <br />
                <br />
                <br />
                <br />
                <br />
              </>
            )}
            {largeContent && <LargeContent data={largeContent} viewportWidth={viewportWidth} />}
            {relatedActivities && relatedActivities.edges.length > 0 && (
              <RelatedActivitiesContainer>
                {relatedActivitiesIntroduction && (
                  <Introduction smallerMargin>
                    <h2 style={{ color: 'var(--dynamic-purple)' }}>
                      {relatedActivitiesIntroduction?.title}
                    </h2>
                    <Markup content={toHTML(relatedActivitiesIntroduction?.summary)} />
                  </Introduction>
                )}
                <SimilarCards
                  similarCards={relatedActivities.edges}
                  pathname={pathname}
                  linkPath="team-building"
                  paddingStyle="0 4.8%"
                  hideBlob
                  hideTitle
                  hideButton
                />
              </RelatedActivitiesContainer>
            )}
            {callToActionBanners && callToActionBanners.length > 0 && (
              <CallToActions
                callToActionBanners={callToActionBanners}
                handleGetQuoteButtonClick={handleGetQuoteButtonClick}
                brochureUrl={brochureUrl}
              />
            )}
            {otherLocations && (
              <OtherLocations
                otherLocations={otherLocations}
                title="Locations"
                gridOrder={locationsGridOrder}
              />
            )}
            {faqs && (
              <FAQContainer>
                <BlobContainer src={BlobYellowSVG} />
                <h4>FAQ</h4>
                <FAQ faqs={faqs} />
              </FAQContainer>
            )}
            {teamBuildingEventTypes && teamBuildingEventTypes.edges.length > 0 && (
              <OtherEventTypes title="Team Building Event Types" otherEventTypes={teamBuildingEventTypes} />
            )}
            <NotPaddedMultiCarousel title="New Case Studies" cards={newCaseStudies} />
            <br />
            <br />
            <br />
            <br />
            {viewportWidth > 971 && (
              <>
                <br />
                <br />
              </>
            )}
            <ClientTestimonialsMultiCarousel
              specificTestimonials={specificTestimonials}
              paddingStyle="42px 3.2% 62px"
              pathname={pathname}
            />
            {highlightedBanner && highlightedBanner.thumbnail && (
              <HighlightBanner
                thumbnail={highlightedBanner.thumbnail}
                action={highlightedBanner.action}
                bannerTitle={highlightedBanner.bannerTitle}
                bannerSubtitle={highlightedBanner.bannerSubtitle}
                bannerSummary={highlightedBanner.bannerSummary}
                bannerButtonText={highlightedBanner.bannerButtonText}
                linkToNavigateTo={highlightedBanner.linkToNavigateTo}
                openLinkInNewTab={highlightedBanner.openLinkInNewTab}
                handleEventManagementTrendsBannerLoading={handleEventManagementTrendsBannerLoading}
                viewportWidth={viewportWidth}
              />
            )}
          </motion.div>
          <ContactUs location={location} />
          <GetQuoteModal
            ref={getQuoteModalRef}
            activityInfo={{
              title,
              formatOfDeliveryAndEventSupport,
              fees,
              productId,
              brochureUrl,
              pathname
            }}
            location={location}
          />
        </>
      )
    );
  }
);

export const Head = ({ data, location }) => {
  const { frontmatter } = data.activity.edges[0].node.childMarkdownRemark;
  const { metaTagOverrides } = frontmatter;
  return (
    <SEO
      pageSpecificTitle={metaTagOverrides?.title ? metaTagOverrides.title : frontmatter.title}
      pageSpecificDescription={
        metaTagOverrides?.description ? metaTagOverrides.description : frontmatter.summary
      }
      pageSpecificThumbnail={
        frontmatter.featuredImg.src.childImageSharp.gatsbyImageData.images.fallback.src
      }
      pathname={location.pathname}
    />
  );
};

const Introduction = styled.div`
  max-width: 914px;
  margin: 3rem auto 0 auto;
  padding: 0 1.5rem;
  z-index: 1;
  h2 {
    color: var(--dynamic-blue);
    font-family: adelle, serif;
    font-size: 1.9rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 2rem;

    @media screen and (min-width: 1143px) {
      font-size: 2rem;
    }
  }

  p {
    color: var(--dynamic-dark-grey);
    font-family: 'proxima-nova', sans-serif;
    font-size: 1.125rem;
    font-weight: 400;
    font-style: normal;
    letter-spacing: 0.01em;
    margin-bottom: 1.75rem;
    text-align: center;
    a {
      color: var(--dynamic-blue);
      text-decoration: underline;
      font-weight: 600;
    }
  }
  @media screen and (min-width: 1200px) {
    font-size: 1.3rem;
    margin: 3rem auto 0 auto;
    padding: 0;
  }
  @media screen and (min-width: 1840px) {
    margin: ${({ smallerMargin }) => (smallerMargin ? '0 auto 0 auto' : '3rem auto 0 auto')};
  }
`;

const RelatedActivitiesContainer = styled.div`
  margin: 3rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  section {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;
  }
  @media screen and (min-width: 1200px) {
    margin: 8rem 0;
  }
`;

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 1.25rem;
  gap: 1rem;
  position: relative;
  margin-top: 8rem;
  h4 {
    text-align: center;
    font-size: 2rem;
    z-index: 1;
  }
`;

const BlobContainer = styled.img`
  left: 50%;
  position: absolute;
  width: auto;
  height: 100%;
  top: -80px;
  transform: translateX(-50%);
  z-index: 0;
  @media screen and (min-width: 768px) {
    top: -80px;
  }
  @media screen and (min-width: 1024px) {
    width: 100%;
    height: auto;
  }
`;

export const query = graphql`
  query ($title: String!, $relatedActivities: [String!]) {
    activity: allFile(
      filter: {
        sourceInstanceName: { eq: "cms_data_activities" }
        ext: { eq: ".md" }
        childMarkdownRemark: { frontmatter: { title: { eq: $title } } }
      }
    ) {
      edges {
        node {
          childMarkdownRemark {
            id
            html
            frontmatter {
              title
              summary
              metaTagOverrides {
                title
                description
              }
              introduction {
                title
                paragraph
              }
              featureVideo {
                platform
                altText
                embedLink
              }
              keyBenefits {
                benefit
                benefitIcon {
                  childImageSharp {
                    gatsbyImageData(
                      height: 36
                      quality: 100
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      layout: FIXED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
              howItWorks {
                title
                summary
                highlights {
                  title
                  description
                  howItWorksIcon {
                    childImageSharp {
                      gatsbyImageData(
                        width: 36
                        height: 36
                        formats: [AUTO, WEBP]
                        transformOptions: { fit: CONTAIN }
                        backgroundColor: "transparent"
                      )
                    }
                  }
                }
              }
              highlightedBanner {
                thumbnail {
                  childImageSharp {
                    gatsbyImageData(
                      layout: FULL_WIDTH
                      quality: 100
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
                bannerTitle
                bannerSubtitle
                bannerSummary
                bannerButtonText
                action
                linkToNavigateTo
                openLinkInNewTab
              }
              callToActionBanners {
                title
                description
                action
                thumbnail {
                  childImageSharp {
                    gatsbyImageData(
                      layout: CONSTRAINED
                      height: 212
                      quality: 100
                      pngOptions: { compressionSpeed: 10 }
                    )
                  }
                }
              }
              productId
              pardotRedirectLinkForBrochure
              featuredImg {
                alt
                src {
                  name
                  childImageSharp {
                    gatsbyImageData(
                      width: 1444
                      transformOptions: { cropFocus: CENTER }
                      quality: 50
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
              carouselImgs {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 710
                      height: 710
                      transformOptions: { cropFocus: CENTER }
                      quality: 65
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
              secondaryCarouselImgs {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 710
                      height: 710
                      transformOptions: { cropFocus: CENTER }
                      quality: 65
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
              whyChooseThisActivity {
                title
                summary
                includeContactUsCTA
                highlights {
                  highlight
                }
              }
              largeContent {
                summary
                title
                tabWidget {
                  richTextContent {
                    content
                    displayRichTextAtTopOfTab
                  }
                  tabTitle
                  tabHighlights {
                    description
                    tabHighlightIcon {
                      childImageSharp {
                        gatsbyImageData(
                          width: 40
                          height: 40
                          layout: FIXED
                          transformOptions: { fit: CONTAIN }
                          backgroundColor: "transparent"
                          formats: [AUTO, WEBP]
                        )
                      }
                    }
                    title
                  }
                }
              }
              relatedActivitiesIntroduction {
                title
                summary
              }
              faqs {
                question
                response
              }
              formatOfDeliveryAndEventSupport
              fees {
                inPersonAndFullyManaged {
                  setupFee
                  perPlayerFee
                }
                inPersonWithRemoteHost {
                  setupFee
                  perPlayerFee
                }
                fullyVirtual {
                  setupFee
                  perPlayerFee
                }
              }
              themes
              approach
              duration
              numbers
              activityLocation
              keyBusinessBenefits
              specificTestimonials {
                title
                personsName
                companyLogo {
                  childImageSharp {
                    gatsbyImageData(
                      width: 60
                      height: 60
                      transformOptions: { fit: CONTAIN }
                      quality: 10
                      backgroundColor: "transparent"
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
                quote
              }
            }
          }
        }
      }
    }
    relatedActivities: allFile(
      limit: 6
      filter: {
        sourceInstanceName: { eq: "cms_data_activities" }
        ext: { eq: ".md" }
        childMarkdownRemark: { frontmatter: { title: { in: $relatedActivities } } }
      }
    ) {
      edges {
        node {
          childMarkdownRemark {
            frontmatter {
              title
              summary
              featuredImg {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(width: 330, height: 200, formats: [AUTO, WEBP])
                  }
                }
              }
            }
          }
        }
      }
    }
    otherLocations: allFile(
      filter: {
        sourceInstanceName: { eq: "cms_data_locations" }
        ext: { eq: ".md" }
        childMarkdownRemark: { frontmatter: { type: { eq: "Team Building" } } }
      }
    ) {
      edges {
        node {
          childMarkdownRemark {
            frontmatter {
              locality
              title
              featuredImg {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 330
                      height: 200
                      transformOptions: { cropFocus: CENTER }
                      quality: 40
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
    locationsGridOrder: file(sourceInstanceName: { eq: "cms_data_settings_grid_orders" }) {
      childMarkdownRemark {
        frontmatter {
          locations {
            title
          }
        }
      }
    }
    teamBuildingEventTypes: allFile(
      filter: {
        sourceInstanceName: { eq: "cms_data_event_types" }
        ext: { eq: ".md" }
        childMarkdownRemark: { frontmatter: { type: { eq: "Team Building" } } }
      }
    ) {
      edges {
        node {
          id
          childMarkdownRemark {
            frontmatter {
              title
              theme
              featuredImg {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 268
                      height: 340
                      transformOptions: { cropFocus: CENTER }
                      quality: 40
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
            }
          }
        }
      }
    }

    newCaseStudies: allFile(
      limit: 10
      filter: {
        sourceInstanceName: { eq: "cms_data_case_studies" }
        ext: { eq: ".md" }
        childMarkdownRemark: { frontmatter: { themes: { in: "Team Building" } } }
      }
      sort: { birthTime: DESC }
    ) {
      edges {
        node {
          id
          childMarkdownRemark {
            frontmatter {
              title
              summary
              featuredImg {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 268
                      height: 340
                      transformOptions: { cropFocus: CENTER }
                      quality: 40
                      pngOptions: { compressionSpeed: 10 }
                      placeholder: BLURRED
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
